import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  alpha,
  Box,
  Button,
  Chip,
  darken,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  lighten,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";
import {
  Add,
  Close,
  Delete,
  Description,
  Edit,
  Search,
} from "@mui/icons-material";
import { AxiosError } from "axios";
import api from "../../../../services/api";
import NewAlertModal from "../../../../componentes/newAlertModal";
import { ReactComponent as EmptyBox } from "../../../../assets/easyReport/iconSvg/emptyBox.svg";
import { useNotification } from "../../../../componentes/notificationProvider";

type PlanilhaStatus = "ATIVA" | "INATIVA" | "ARQUIVADA";

type Planilha = {
  id: string;
  spreadsheetId: string;
  spreadsheetName: string;
  acessoClientesId: string;
  status: PlanilhaStatus;
  ultimaAtualizacao?: string | null;
  createAt?: string;
  updateAt?: string | null;
};
type OutletContextType = {
  setSubTitle: (val: string) => void;
};
const STATUS_OPTIONS: PlanilhaStatus[] = ["ATIVA", "INATIVA", "ARQUIVADA"];

const statusColor = (status: PlanilhaStatus, theme: Theme): string => {
  if (status === "ATIVA") return theme.palette.success.main;
  if (status === "ARQUIVADA") return theme.palette.warning.main;
  return theme.palette.text.disabled;
};

const emptyForm = {
  spreadsheetId: "",
  spreadsheetName: "",
  status: "ATIVA" as PlanilhaStatus,
};

export default function EasyFlowPlanilhas() {
  const theme = useTheme();
  const notify = useNotification();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";

  const { setSubTitle } = useOutletContext<OutletContextType>();

  const [planilhas, setPlanilhas] = useState<Planilha[]>([]);
  const [loadingPlanilhas, setLoadingPlanilhas] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pageSize, setPageSize] = useState(10);

  const [openFormModal, setOpenFormModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [isSubmit, setIsSubmit] = useState(false);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedPlanilha, setSelectedPlanilha] = useState<Planilha | null>(
    null,
  );

  useEffect(() => {
    setSubTitle("EasyFlow");
  }, [setSubTitle]);
  // Bloqueio de acesso a não-administradores (mesma regra da tela de usuários)
  useEffect(() => {
    const codigo = JSON.parse(String(localStorage.getItem("1"))).usuarioCliente
      .codigoPermissao;
    if (codigo.indexOf("adm001") === -1 && codigo.indexOf("adm002") === -1) {
      navigate("/login");
    }
  }, [navigate]);

  const styles = {
    containerBox: {
      minWidth: "320px",
      display: "flex",
      flexDirection: "column",
      gap: 2,
      height: "100%",
    },
    titlePage: {
      fontWeight: 400,
      fontSize: "1.2rem",
      color: theme.palette.text.primary,
    },
    subtitlePage: {
      color: theme.palette.text.secondary,
      mb: 1,
    },
    paper: {
      padding: 2,
      gap: 2,
      width: "100%",
      display: "flex",
      flexDirection: "column",
    },
    boxHeader: {
      width: "100%",
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 2,
      alignItems: "center",
      justifyContent: "space-between",
    },
    clienteAutocomplete: {
      width: { xs: "100%", md: "420px" },
    },
    searchInput: {
      width: { xs: "100%", md: "360px" },
    },
    button: {
      fontSize: "13px",
      height: "40px",
      whiteSpace: "nowrap",
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
      color: "primary.main",
    },
    boxEmpty: {
      p: 2,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
    },
    icon: {
      width: "140px",
      height: "140px",
      fill: theme.palette.text.disabled,
    },
    dialogField: { mt: 1 },
    boxTitle: {
      display: "flex",
      alignItems: "center",
      flexDirection: "row",
      gap: 1.5,
    },
    boxRowFields: {
      display: "flex",
      flexDirection: "column",
      with: "100%",
      gap: 1,
    },
    subtitle: {
      fontSize: "14px",
      fontWeight: 500,
      color: "text.primary",
    },
    dataGrid: {
      width: "100%",
      height: "100%",
      minHeight: 280,
      fontSize: 12,
      borderRadius: "7px",
      border: 0,
      backgroundColor: theme.palette.background.paper,
      "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
        outline: "none !important",
      },
      ".MuiDataGrid-cell": {
        display: "flex",
        alignItems: "center",
        borderColor: theme.palette.background.tab,
      },
      "& .MuiDataGrid-scrollbar--vertical": {
        display: "none",
      },
      "& .MuiDataGrid-filler": {
        display: "none",
      },
      "& .MuiDataGrid-scrollbarFiller--borderTop": {
        display: "none",
      },
      "& .MuiDataGrid-scrollbarFiller--header": {
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.background.tab}`,
      },
      "& .MuiDataGrid-columnHeaderTitle": {
        color: "text.primary",
      },
      "& .MuiDataGrid-footerContainer": {
        backgroundColor: "transparent",
        border: 0,
        borderTop: `1px solid ${theme.palette.background.tab}`,
        height: "45px",
      },
      "& .MuiDataGrid-columnHeader": {
        backgroundColor: theme.palette.background.paper,
        border: 0,
        borderTop: `1px solid ${theme.palette.background.tab}`,
      },
      "& .MuiDataGrid-topContainer": {
        border: 0,
        backgroundColor: theme.palette.background.paper,
      },
      "& .MuiDataGrid-row--borderBottom": {
        border: 0,
      },
      "& .MuiDataGrid-columnHeaders": {
        border: 0,
        height: "55px",
        backgroundColor: theme.palette.background.paper,
      },
      "& .even-row": {
        backgroundColor: theme.palette.background.card,
      },
      "& .odd-row": {
        backgroundColor: theme.palette.background.paper,
      },
    },
  };

  async function fetchPlanilhas() {
    try {
      setLoadingPlanilhas(true);
      const response = await api.get("/easyFlow2.0/spreadsheets");
      setPlanilhas(
        Array.isArray(response.data?.data) ? response.data.data : [],
      );
    } catch (error) {
      if (error instanceof AxiosError)
        notify(
          error.response?.data?.error || "Erro ao carregar planilhas",
          "error",
          3000,
        );
      else notify("Não foi possível se conectar ao servidor!", "error", 3000);
      setPlanilhas([]);
    } finally {
      setLoadingPlanilhas(false);
    }
  }

  useEffect(() => {
    fetchPlanilhas();
  }, []);

  const filteredPlanilhas = useMemo(() => {
    const search = searchText.toLowerCase();
    return planilhas.filter(
      (p) =>
        p.spreadsheetName?.toLowerCase().includes(search) ||
        p.spreadsheetId?.toLowerCase().includes(search),
    );
  }, [planilhas, searchText]);

  const handleOpenCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setIsSubmit(false);
    setOpenFormModal(true);
  };

  const handleOpenEdit = (planilha: Planilha) => {
    setEditingId(planilha.id);
    setForm({
      spreadsheetId: planilha.spreadsheetId,
      spreadsheetName: planilha.spreadsheetName,
      status: planilha.status,
    });
    setIsSubmit(false);
    setOpenFormModal(true);
  };

  const handleSave = async () => {
    setIsSubmit(true);

    if (!form.spreadsheetName.trim()) return;
    if (!editingId && !form.spreadsheetId.trim()) return;

    try {
      setSaving(true);
      if (editingId) {
        await api.put(`/easyFlow2.0/spreadsheets/${editingId}`, {
          spreadsheetName: form.spreadsheetName.trim(),
          status: form.status,
        });
        notify("Planilha atualizada com sucesso!", "success", 3000);
      } else {
        await api.post(`/easyFlow2.0/spreadsheets`, {
          spreadsheetId: form.spreadsheetId.trim(),
          spreadsheetName: form.spreadsheetName.trim(),
          status: form.status,
        });
        notify("Planilha vinculada com sucesso!", "success", 3000);
      }
      setOpenFormModal(false);
      fetchPlanilhas();
    } catch (error) {
      if (error instanceof AxiosError)
        notify(
          error.response?.data?.error || "Erro ao salvar planilha",
          "error",
          3000,
        );
      else notify("Não foi possível se conectar ao servidor!", "error", 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPlanilha?.id) return;
    try {
      await api.delete(`/easyFlow2.0/spreadsheets/${selectedPlanilha.id}`);
      setPlanilhas((prev) => prev.filter((p) => p.id !== selectedPlanilha.id));
      notify("Planilha removida com sucesso!", "success", 3000);
    } catch (error) {
      if (error instanceof AxiosError)
        notify(
          error.response?.data?.error || "Erro ao remover planilha",
          "error",
          3000,
        );
      else notify("Não foi possível se conectar ao servidor!", "error", 3000);
    } finally {
      setOpenDeleteModal(false);
    }
  };

  const columns: GridColDef<Planilha>[] = [
    {
      flex: 1,
      minWidth: 180,
      field: "spreadsheetName",
      headerName: "Nome",
    },
    {
      flex: 1.5,
      minWidth: 220,
      field: "spreadsheetId",
      headerName: "ID da Planilha",
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography fontSize={12} noWrap>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      width: 140,
      field: "status",
      headerName: "Status",
      renderCell: (params) => {
        if (!params.value) return null;
        const color = statusColor(params.value, theme);
        return (
          <Chip
            size="small"
            label={params.value}
            sx={{
              border: "1px solid",
              borderColor: color,
              backgroundColor: alpha(color, 0.15),
              color: isDark ? lighten(color, 0.5) : darken(color, 0.5),
              height: "22px",
              fontWeight: 500,
            }}
          />
        );
      },
    },
    {
      width: 110,
      field: "actions",
      headerName: "Ações",
      align: "right",
      headerAlign: "right",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" height="100%">
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => handleOpenEdit(params.row)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton
              size="small"
              onClick={() => {
                setSelectedPlanilha(params.row);
                setOpenDeleteModal(true);
              }}
            >
              <Delete fontSize="small" color="error" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  function CustomNoRowsOverlay() {
    return (
      <Box sx={styles.boxEmpty}>
        <EmptyBox style={styles.icon} />
        <Typography color="text.disabled">
          Nenhuma planilha vinculada.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={styles.containerBox}>
        <Paper elevation={0} sx={{ p: 2 }}>
          <Stack direction="column">
            <Typography sx={styles.titlePage}>Planilhas easyFlow</Typography>
            <Typography variant="body2" sx={styles.subtitlePage}>
              Gerencie as planilhas do Google Sheets. Apenas uma planilha pode
              ficar ATIVA.
            </Typography>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={styles.paper}>
          <Box sx={styles.boxHeader}>
            <TextField
              size="small"
              placeholder="Pesquisar planilha"
              sx={styles.searchInput}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              autoComplete="off"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              startIcon={<Add />}
              onClick={handleOpenCreate}
              sx={styles.button}
              variant="contained"
            >
              Vincular planilha
            </Button>
          </Box>

          <DataGrid
            sx={styles.dataGrid}
            rows={filteredPlanilhas}
            columns={columns}
            loading={loadingPlanilhas}
            disableColumnMenu
            pageSizeOptions={[10, 25, 50, 100]}
            onPaginationModelChange={(model) => setPageSize(model.pageSize)}
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0
                ? "even-row"
                : "odd-row"
            }
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
            }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: pageSize },
              },
            }}
            disableRowSelectionOnClick
            density="compact"
          />
        </Paper>
      </Box>

      {/* Modal de criar / editar planilha */}
      <Dialog
        open={openFormModal}
        onClose={() => setOpenFormModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: "row",
            width: "100%",
          }}
        >
          <Stack
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "row",
              gap: 1.5,
            }}
          >
            <Stack
              sx={{
                p: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 1.5,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Description color="primary" sx={{ fontSize: 22 }} />
            </Stack>
            <Stack>
              <Typography
                fontWeight="bold"
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: "1.05rem",
                  lineHeight: 1.2,
                }}
              >
                {editingId ? "Editar planilha" : "Vincular nova planilha"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {editingId ? form.spreadsheetName : "Nova planilha"}
              </Typography>
            </Stack>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={() => setOpenFormModal(false)}
            disabled={saving}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={1}>
            <Box sx={styles.boxRowFields}>
              <Typography sx={styles.subtitle}>Nome</Typography>{" "}
              <TextField
                placeholder="Nome da planilha"
                fullWidth
                value={form.spreadsheetName}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    spreadsheetName: e.target.value,
                  }))
                }
                error={isSubmit && !form.spreadsheetName.trim()}
                helperText={
                  isSubmit && !form.spreadsheetName.trim()
                    ? "Campo obrigatório"
                    : ""
                }
              />
            </Box>
            <Box sx={styles.boxRowFields}>
              <Typography sx={styles.subtitle}>ID da planilha</Typography>{" "}
              <TextField
                placeholder="ID da planilha (Google Sheets)"
                fullWidth
                value={form.spreadsheetId}
                disabled={!!editingId}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    spreadsheetId: e.target.value,
                  }))
                }
                error={isSubmit && !editingId && !form.spreadsheetId.trim()}
                helperText={
                  editingId
                    ? "O ID da planilha não pode ser alterado."
                    : isSubmit && !form.spreadsheetId.trim()
                      ? "Campo obrigatório"
                      : ""
                }
              />
            </Box>
            <Box sx={styles.boxRowFields}>
              <Typography sx={styles.subtitle}>Status</Typography>{" "}
              <TextField
                select
                placeholder="Status"
                fullWidth
                value={form.status}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    status: e.target.value as PlanilhaStatus,
                  }))
                }
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            {form.status === "ATIVA" && (
              <Typography variant="caption" color="text.secondary">
                Ao salvar como ATIVA, qualquer outra planilha ATIVA será marcada
                como INATIVA.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenFormModal(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {editingId ? "Atualizar" : "Vincular"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmação de exclusão */}
      <NewAlertModal
        openModal={openDeleteModal}
        setOpenModal={setOpenDeleteModal}
        buttonText="Excluir"
        buttonCancelText="Cancelar"
        confirmar={false}
        handleButton={handleDelete}
        handleCancel={() => setOpenDeleteModal(false)}
        severity="error"
        singleButton={false}
        textContent={`Tem certeza que deseja excluir a planilha "${selectedPlanilha?.spreadsheetName}"?`}
      />
    </>
  );
}
